import DateSelector from '@/components/features/shared/DateSelector'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DeathData, DeathRelationship, DeathState, formatDeathRelationship, formatDeathState, TransferredTo } from '@/domain/deathData/deathData'
import { deathDataSchema } from '@/domain/deathData/deathDataSchemas'
import { ServiceType } from '@/domain/offerings/offering'
import { DocType } from '@/domain/shared'
import { formatDocNumber } from '@/utils/helpers'
import { zodResolver } from '@hookform/resolvers/zod'
import { AlertCircle } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

interface ManualEditDeathDataModalProps {
  isOpen: boolean
  onClose: () => void
  record: DeathData | null
  onSave: (data: DeathData) => void
  serviceType?: ServiceType
}

export const ManualEditDeathDataModal = ({ isOpen, onClose, record, onSave, serviceType }: ManualEditDeathDataModalProps) => {
  const isEditing = !!record && !!record.id
  const isCremation = serviceType === ServiceType.CREMACIONES

  const schema = deathDataSchema(serviceType || ServiceType.PARCELAS)

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      docType: undefined,
      docNumber: '',
      birthDate: undefined,
      deathDate: undefined,
      state: isCremation ? DeathState.CREMATED : DeathState.BURIED,
      notes: '',
      intermentDate: undefined,
      cremationDate: undefined,
      disintermentDate: undefined,
      reductionDate: undefined,
      relationshipWithDeceased: undefined,
      thumbLevel: undefined,
      entryNumber: '',
      volume: '',
      year: '',
      leaf: '',
      office: '',
      mortuary: '',
      medicOfDeceased: '',
      deathCertificateAuthorization: '',
      transferredTo: undefined
    }
  })

  // console.log(form.formState.errors, form.getValues(), record)

  useEffect(() => {
    if (isOpen) {
      if (record) {
        form.reset({
          name: record.name || '',
          docType: (record.docType as DocType) || undefined,
          docNumber: record.docNumber || '',
          birthDate: record.birthDate,
          deathDate: record.deathDate,
          state: record.state || DeathState.BURIED,
          notes: record.notes || undefined,
          intermentDate: record.intermentDate || undefined,
          cremationDate: record.cremationDate || undefined,
          disintermentDate: record.disintermentDate || undefined,
          reductionDate: record.reductionDate || undefined,
          relationshipWithDeceased: record.relationshipWithDeceased || undefined,
          thumbLevel: record.thumbLevel ? record.thumbLevel : undefined,
          entryNumber: record.entryNumber || '',
          volume: record.volume || '',
          year: record.year?.toString() || '',
          leaf: record.leaf || '',
          office: record.office || '',
          mortuary: record.mortuary || '',
          medicOfDeceased: record.medicOfDeceased || '',
          deathCertificateAuthorization: record.deathCertificateAuthorization || '',
          transferredTo: record.transferredTo || undefined
        })
      } else {
        form.reset({
          name: '',
          docType: undefined,
          docNumber: '',
          birthDate: undefined,
          deathDate: undefined,
          state: isCremation ? DeathState.CREMATED : DeathState.BURIED,
          notes: undefined,
          intermentDate: undefined,
          cremationDate: undefined,
          disintermentDate: undefined,
          reductionDate: undefined,
          relationshipWithDeceased: undefined,
          thumbLevel: undefined,
          entryNumber: '',
          volume: '',
          year: undefined,
          leaf: '',
          office: '',
          mortuary: '',
          medicOfDeceased: '',
          deathCertificateAuthorization: '',
          transferredTo: undefined
        })
      }
    }
  }, [isOpen, record, form])

  const onSubmit = form.handleSubmit((data) => {
    onSave({
      ...(record || {}),
      ...data as any,
      transferredTo: data.transferredTo || null,
      state: data.state || DeathState.DEATH
    })
    onClose()
  })

  const birthDate = form.watch('birthDate')
  const deathDate = form.watch('deathDate')
  const intermentDate = form.watch('intermentDate')
  const cremationDate = form.watch('cremationDate')
  const disintermentDate = form.watch('disintermentDate')
  const reductionDate = form.watch('reductionDate')
  const state = form.watch('state')

  const handleDocNumberChange = (value: string) => {
    const docType = form.getValues("docType") as DocType || undefined
    form.setValue('docNumber', formatDocNumber(value, docType))
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isCremation ? 'Editar Fallecido' : isEditing ? 'Editar Ocupante' : 'Nuevo Ocupante'}</DialogTitle>
          <DialogDescription>
            {isCremation ? 'Modifique los datos del fallecido' : isEditing ? 'Modifique los datos del ocupante de la parcela' : 'Ingrese los datos del nuevo ocupante'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-6 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Apellido, Nombre</label>
              <input
                type="text"
                className={`w-full p-2 border rounded-md ${form.formState.errors.name ? 'border-red-500' : ''}`}
                {...form.register('name')}
              />
              {form.formState.errors.name && <p className="text-red-500 text-xs">{form.formState.errors.name.message as string}</p>}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Parentesco del fallecido con el titular</label>
              <Select onValueChange={(value) => form.setValue('relationshipWithDeceased', value as DeathRelationship)} value={form.watch('relationshipWithDeceased') || ''}>
                <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(DeathRelationship).map(([key, value]) => (
                    <SelectItem key={key} value={value}>{formatDeathRelationship(value)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.relationshipWithDeceased && <p className="text-red-500 text-xs">{form.formState.errors.relationshipWithDeceased.message as string}</p>}
            </div>

            {!isCremation && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Nivel en Parcela (1-3)</label>
                <input
                  type="number"
                  min="1"
                  max="3"
                  className={`w-full p-2 border rounded-md ${form.formState.errors.thumbLevel ? 'border-red-500' : ''}`}
                  value={form.watch('thumbLevel') || ''}
                  onChange={(e) => form.setValue('thumbLevel', e.target.value ? parseInt(e.target.value) : undefined)}
                />
                {form.formState.errors.thumbLevel && <p className="text-red-500 text-xs">{form.formState.errors.thumbLevel.message as string}</p>}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Tipo de documento</label>
              <Select onValueChange={(value) => form.setValue('docType', value as DocType)} value={form.watch('docType') || ''}>
                <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  {Object.values(DocType).map((dt) => (
                    <SelectItem key={dt} value={dt}>{dt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Número de documento</label>
              <input
                type="text"
                className={`w-full p-2 border rounded-md ${form.formState.errors.docNumber ? 'border-red-500' : ''}`}
                value={form.watch('docNumber') || ''}
                onChange={(e) => handleDocNumberChange(e.target.value)}
              />
              {form.formState.errors.docNumber && <p className="text-red-500 text-xs">{form.formState.errors.docNumber.message as string}</p>}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Fecha de nacimiento</label>
              <DateSelector
                isUnderModal
                key={`birth-${birthDate?.getTime() || 'empty'}`}
                initialDate={birthDate as Date}
                onDateSelected={(date) => form.setValue('birthDate', date)}
              />
              {form.formState.errors.birthDate && <p className="text-red-500 text-xs">{form.formState.errors.birthDate.message as string}</p>}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Fecha de defunción</label>
              <DateSelector
                isUnderModal
                key={`death-${deathDate?.toString() || 'empty'}`}
                initialDate={deathDate as Date}
                onDateSelected={(date) => form.setValue('deathDate', date)}
              />
              {form.formState.errors.deathDate && <p className="text-red-500 text-xs">{form.formState.errors.deathDate.message as string}</p>}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Estado</label>
              <Select onValueChange={(value) => form.setValue('state', value as DeathState)} value={form.watch('state') || ''} disabled={isCremation}>
                <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  {Object.values(DeathState).filter(ds => ds !== DeathState.ALIVE && ds !== DeathState.DEATH).map((ds) => (
                    <SelectItem key={ds} value={ds}>
                      {formatDeathState(ds)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 flex flex-col justify-center">
              {!state || state === DeathState.DEATH ? (
                <Alert variant="info" className="py-3 mt-3">
                  <AlertDescription className="text-xs flex items-center gap-3">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    Seleccione un estado para habilitar el registro de la fecha.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="flex flex-col justify-center">
                  {state === DeathState.BURIED && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Fecha de inhumación</label>
                      <DateSelector
                        isUnderModal
                        key={`interment-${intermentDate?.toString() || 'empty'}`}
                        initialDate={intermentDate as Date}
                        onDateSelected={(date) => form.setValue('intermentDate', date)}
                      />
                    </div>
                  )}

                  {state === DeathState.CREMATED && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Fecha de cremación</label>
                      <DateSelector
                        isUnderModal
                        key={`cremation-${cremationDate?.toString() || 'empty'}`}
                        initialDate={cremationDate as Date}
                        onDateSelected={(date) => form.setValue('cremationDate', date)}
                      />
                    </div>
                  )}

                  {state === DeathState.REDUCED && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Fecha de reducción</label>
                      <DateSelector
                        isUnderModal
                        key={`reduction-${reductionDate?.toString() || 'empty'}`}
                        initialDate={reductionDate as Date}
                        onDateSelected={(date) => form.setValue('reductionDate', date)}
                      />
                    </div>
                  )}

                  {state === DeathState.EXHUMED && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Fecha de exhumación</label>
                      <DateSelector
                        isUnderModal
                        key={`disinterment-${disintermentDate?.toString() || 'empty'}`}
                        initialDate={disintermentDate as Date}
                        onDateSelected={(date) => form.setValue('disintermentDate', date)}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2 col-span-full">
              <label className="text-sm font-medium">Notas</label>
              <textarea className="w-full p-2 border rounded-md" {...form.register('notes')} />
            </div>

            <div className="space-y-2 col-span-full">
              <label className="text-sm font-medium">Trasladado a otro cementerio</label>
              <Select onValueChange={(value) => form.setValue('transferredTo', value)} value={form.watch('transferredTo') || ''}>
                <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  {Object.values(TransferredTo).map((v) => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold mb-3">Datos de Registro</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium">Acta Nro</label>
                <input type="text" className="w-full p-2 border rounded-md text-sm" {...form.register('entryNumber')} />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium">Tomo</label>
                <input type="text" className="w-full p-2 border rounded-md text-sm" {...form.register('volume')} />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium">Año</label>
                <input type="number" className="w-full p-2 border rounded-md text-sm"
                  value={form.watch('year') || undefined}
                  onChange={(e) => form.setValue('year', e.target.value ? parseInt(e.target.value)?.toString() : undefined)} />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium">Folio</label>
                <input type="text" className="w-full p-2 border rounded-md text-sm" {...form.register('leaf')} />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium">Oficina/Delegación</label>
                <input type="text" className="w-full p-2 border rounded-md text-sm" {...form.register('office')} />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium">Funeraria</label>
                <input type="text" className="w-full p-2 border rounded-md text-sm" {...form.register('mortuary')} />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium">Médico</label>
                <input type="text" className="w-full p-2 border rounded-md text-sm" {...form.register('medicOfDeceased')} />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium">Autorización de Sepultura</label>
                <input type="text" className="w-full p-2 border rounded-md text-sm" {...form.register('deathCertificateAuthorization')} />
              </div>
            </div>
          </div>

          <div className="flex justify-between gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" variant="secondary">Guardar</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
